<template>
  <!-- The app's range picker, opened from the dropdown itself: no date inputs, the range shows in the dropdown. -->
  <DatePicker
    class="date-filter-select"
    :class="{ 'date-filter-select--block': block }"
    :value="row.from ? { start: row.from, end: row.to || row.from } : null"
    is-range
    mode="date"
    :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
    :columns="$screens({ default: 1, lg: 2 })"
    v-bind="calendar"
    :popover="{ visibility: 'click', placement: 'bottom-start' }"
    @input="setRange"
    @popoverDidHide="onPickerClosed"
  >
    <template #default="{ showPopover }">
      <b-form-select
        :value="selected"
        :options="options"
        :aria-label="label"
        :data-cy="`${dataCy}-preset`"
        class="date-filter-select__preset"
        @change="pick($event, showPopover)"
      />
    </template>
  </DatePicker>
</template>

<script lang="ts">
import moment from 'moment'
import { DatePicker } from 'v-calendar'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { FilterRow } from '@/views/Groups/types'
import {
  CUSTOM_RANGE,
  DATE_PRESETS
} from '@/views/MessageCenter/groupFilterRows.js'

/**
 * A date filter: presets that move with each day, and "Custom range…", which opens the app's
 * range picker from the dropdown and shows the picked range in it.
 * Writes the row's preset, from and to.
 */
@Component({ components: { DatePicker } })
export default class DateFilterSelect extends Vue {
  @Prop({ type: Object, required: true }) row!: FilterRow
  @Prop({ type: String, required: true }) label!: string
  /** What the empty choice says: "Any date" where no filter is fine, a prompt where one is needed. */
  @Prop({ type: String, default: 'Any date' }) emptyText!: string
  @Prop({ type: String, default: 'date-filter' }) dataCy!: string
  /** Full width, for a form card's column rather than a row of controls. */
  @Prop({ type: Boolean, default: false }) block!: boolean

  /** The dropdown's own value: the row's preset, except while "Change custom range…" is being handled. */
  selected: string | null = this.row.preset
  /** The custom range is being picked; closing the picker without a range undoes the choice. */
  picking = false
  /** The choice before "Custom range…", restored when the picker closes with nothing picked. */
  before: {
    preset: string | null
    from: string | null
    to: string | null
  } | null = null

  @Watch('row.preset')
  onPreset(preset: string | null) {
    this.selected = preset
  }

  /** A birthday repeats every year: its calendar starts at January, names no year, and runs to next January. */
  get calendar() {
    if (this.row.field !== 'birthday') return {}
    const year = moment().year()

    return {
      masks: { title: 'MMMM' },
      fromPage: { month: 1, year },
      minDate: new Date(year, 0, 1),
      maxDate: new Date(year + 1, 0, 31)
    }
  }

  get options() {
    const from = this.row.from || this.row.to
    const custom = this.row.preset === CUSTOM_RANGE && from && this.customText

    // No dates on the presets: "Today · Oct 16" read as a fixed Oct 16, and a saved group's preset moves.
    return [
      { value: null, text: this.emptyText },
      {
        label: 'Moves with each day',
        options: (DATE_PRESETS[this.row.field] ?? []).map(
          ({ value, text }: { value: string; text: string }) => ({
            value,
            text
          })
        )
      },
      {
        label:
          this.row.field === 'birthday'
            ? 'Same dates every year'
            : 'Fixed dates',
        options: custom
          ? [
              { value: CUSTOM_RANGE, text: `Custom · ${custom}` },
              { value: CHANGE_RANGE, text: 'Change custom range…' }
            ]
          : [{ value: CUSTOM_RANGE, text: 'Custom range…' }]
      }
    ]
  }

  /** The picked range: no year on a birthday, which repeats; a range saved before fixed dates still moves. */
  get customText(): string {
    const { from, to, offsets, field } = this.row
    const format = field === 'birthday' ? 'MMM D' : 'MMM D, YYYY'
    const [start, end] = [from || to, to || from].map(date =>
      moment(date).format(format)
    )
    const text = start === end ? start : `${start} – ${end}`
    const moving = offsets && offsets.from === from && offsets.to === to

    return moving ? `${text} (moves daily)` : text
  }

  pick(choice: string | null, showPopover: () => void) {
    if (choice !== CUSTOM_RANGE && choice !== CHANGE_RANGE) {
      this.row.preset = choice
      this.row.from = null
      this.row.to = null

      return
    }
    const { preset, from, to } = this.row
    this.before = { preset, from, to }
    this.picking = true
    this.row.preset = CUSTOM_RANGE
    // The dropdown shows "Custom · …" again rather than the one-off "Change custom range…".
    this.selected = choice
    this.$nextTick(() => {
      this.selected = CUSTOM_RANGE
      showPopover()
    })
  }

  setRange(range: { start: string; end: string } | null) {
    if (!range) return
    this.row.from = range.start
    this.row.to = range.end
    this.picking = false
  }

  onPickerClosed() {
    if (!this.picking || !this.before) return
    this.picking = false
    Object.assign(this.row, this.before)
    this.selected = this.row.preset
  }
}

// A menu choice only: it re-opens the picker on a custom range already set.
const CHANGE_RANGE = 'change-range'
</script>

<style lang="scss" scoped>
.date-filter-select {
  display: inline-block;
}

.date-filter-select__preset {
  width: auto;
  min-width: 200px;
}

.date-filter-select--block,
.date-filter-select--block .date-filter-select__preset {
  display: block;
  width: 100%;
}
</style>
